import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdNotifications, MdCheckCircle, MdClose, MdPoll, MdStar, MdFlag, MdSecurity, MdPerson, MdInfo } from 'react-icons/md';

const ICON_MAP = {
  poll_approved: MdCheckCircle,
  poll_rejected: MdClose,
  poll_flagged: MdFlag,
  poll_closed: MdPoll,
  badge: MdStar,
  achievement: MdStar,
  security: MdSecurity,
  profile_reset: MdPerson,
};

const COLOR_MAP = {
  poll_approved: 'text-green-500 bg-green-50',
  poll_rejected: 'text-red-500 bg-red-50',
  poll_flagged: 'text-orange-500 bg-orange-50',
  poll_closed: 'text-blue-500 bg-blue-50',
  badge: 'text-yellow-500 bg-yellow-50',
  achievement: 'text-yellow-500 bg-yellow-50',
  security: 'text-red-500 bg-red-50',
  profile_reset: 'text-purple-500 bg-purple-50',
};

function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/notifications?limit=10`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Silently fail - table might not exist
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/mark-read`, { notification_ids: [id] });
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const getIcon = (type) => {
    const Icon = ICON_MAP[type] || MdInfo;
    return Icon;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <MdNotifications className={`text-xl ${unreadCount > 0 ? 'text-gray-800' : 'text-gray-600'}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-black leading-none"
            data-testid="notification-unread-badge"
            aria-label={`${unreadCount} unread notifications`}
          >
            !
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 font-rubik text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-primary hover:text-primary-dark font-medium"
                data-testid="mark-all-read-btn"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center" data-testid="notification-empty">
                <MdNotifications className="mx-auto text-3xl text-gray-300 mb-2" />
                <p className="text-gray-400 text-sm font-rubik">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = getIcon(notif.type);
                const colors = COLOR_MAP[notif.type] || 'text-gray-500 bg-gray-50';
                const [iconColor, iconBg] = colors.split(' ');
                
                return (
                  <div
                    key={notif.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notif.is_read ? 'bg-blue-50/40' : ''
                    }`}
                    onClick={() => { if (!notif.is_read) markRead(notif.notification_id); }}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                      <Icon className={`text-base ${iconColor}`} />
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className={`text-sm leading-snug ${!notif.is_read ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                        {notif.title}
                      </p>
                      {notif.message && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t bg-gray-50 text-center">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-primary hover:text-primary-dark font-medium font-rubik"
                data-testid="view-all-notifications-link"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
